import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import PropTypes from 'prop-types';
import { colors } from 'constants/colors';
import { SubscriptionStatus } from 'constants/subscription';
import { useMyAdditionalExportsFetch, useSubscription } from 'hooks';
import { useAuthSelector } from 'stores';

import { selectOptions } from './constants';
import { CustomFormControl, MenuItemCustom } from './ExportSelect.styles';

const ExportSelect = ({ handleSelected, isSelected, getFileValue }) => {
  const { user } = useAuthSelector();
  const { subscriptionStatus } = useSubscription();
  const userType = user?.type;

  // Task 34 — export addizionali effettivi dell'utente loggato, non più
  // sincroni da user.additionalExports/user.parent.additionalExports (store
  // auth): il BE li ha spostati su un endpoint dedicato, già espansi con la
  // gerarchia padre→figli (es. 15 concesso → anche 19/21/23/26). `loading`
  // tiene disabilitata la tendina finché il fetch non risolve, così non la
  // si apre prima che arrivi la risposta.
  const { data: meAdditionalExports, loading: additionalExportsLoading } =
    useMyAdditionalExportsFetch();

  // Admin > Gestione Utenti > Dettaglio Utente, campo "Export addizionali" —
  // impostabile solo sull'utente principale, ma effettivo anche per i suoi
  // collaboratori interni: per questi ultimi GET users/me/additional-exports
  // restituisce già le concessioni del principale (vedi commento sul fetch
  // sopra), nessuna risalita manuale a user.parent qui.
  const userAdditionalExports = meAdditionalExports?.additionalExports ?? [];

  return (
    <div>
      <CustomFormControl style={{ width: '100%' }}>
        <InputLabel>Tipologia esportazione</InputLabel>
        <Select disabled={additionalExportsLoading}>
          <MenuItemCustom
            value=""
            onClick={() => isSelected && handleSelected(false)}
          >
            <em>Nessun valore</em>
          </MenuItemCustom>

          {selectOptions?.map((option, idx) => {
            // Admin > Dettaglio Utente, "Export addizionali" — sblocca
            // un'esportazione normalmente non visibile/abilitata per questo
            // userType/sottoscrizione, data-driven per singolo id.
            const isAdditionalExport =
              Boolean(option.value) &&
              userAdditionalExports.includes(option.value);
            if (
              (option.visible && option.visible?.includes(userType)) ||
              !option.visible ||
              isAdditionalExport
            ) {
              const checkDisabledForSubscription =
                option.disabledIfNoSubscription
                  ? subscriptionStatus !== SubscriptionStatus.ATTIVA
                  : false;
              // Issue #1033 — flag distinto da `disabledIfNoSubscription`:
              // gli export L3 (Sintesi/Relazione L3 Idraulico) restano
              // attivi per chi ha sottoscrizione generica attiva ma nessun
              // modulo L3, se si guarda solo subscriptionStatus. Il BE
              // (UserExtensions.checkHasL3Module, risolto anche per i
              // collaboratori interni) espone già l'entitlement corretto su
              // user.hasL3Module — va usato quello, non lastSubscription.level.
              const checkDisabledForL3Module = option.disabledIfNoL3Module
                ? !user?.hasL3Module
                : false;
              if (option.value === null || option.value === 0) {
                if (option.value === null) {
                  // titolo categoria
                  return (
                    <MenuItemCustom
                      key={idx}
                      disabled
                      value={option.value}
                      style={{
                        fontSize: 13,
                        fontWeight: 'bold',
                        color: colors.BLACK,
                        opacity: 1,
                      }}
                    >
                      {option.label}
                    </MenuItemCustom>
                  );
                } else {
                  // titolo sotto categoria
                  return (
                    <MenuItemCustom
                      key={idx}
                      disabled
                      value={option.value}
                      style={{
                        fontSize: 17,
                        fontWeight: 'bold',
                        color: colors.BLACK,
                        textTransform: 'uppercase',
                        opacity: 1,
                      }}
                    >
                      {option.label}
                    </MenuItemCustom>
                  );
                }
              } else {
                // opzione effettiva
                return (
                  <MenuItemCustom
                    key={idx}
                    value={option.value}
                    disabled={
                      option.disabled ||
                      (checkDisabledForSubscription && !isAdditionalExport) ||
                      (checkDisabledForL3Module && !isAdditionalExport)
                    }
                    onClick={() => {
                      handleSelected(true);
                      getFileValue(option.value);
                    }}
                  >
                    {option.label}
                  </MenuItemCustom>
                );
              }
            }
          })}
        </Select>
      </CustomFormControl>
    </div>
  );
};

export { ExportSelect };

ExportSelect.propTypes = {
  handleSelected: PropTypes.func,
  isSelected: PropTypes.bool,
  getFileValue: PropTypes.func,
};
